import React, { useContext, useState } from "react"

import { Button, Divider, Flex, Select, Spin, Tooltip, Typography } from "antd"
import { CheckCircleTwoTone, CloseOutlined, DownloadOutlined, ExclamationCircleTwoTone, WarningTwoTone } from "@ant-design/icons"

import { DdmOrgsContext } from "../../contexts"

export default ({
  status,
  closeModal,
  sendLayers,
  selectedOrg,
  selectOrg,
  ddmOrgs,
  preflightFailureMessage,
  downloadAssets,
  enableDownloadAssets,
  zipFiles,
  noticeName,
  pmName,
}) => {
  let title = 'Migrate to Didomi'
  let showSpinners = false
  let header
  let subheader
  let screenshotText = 'Screenshot'
  let htmlText = 'HTML'
  let cssText = 'CSS'
  let selectOpacity = .5
  let formDisabled = true
  let headerIcon
  let panelColor = '#e1edfa'
  let failed = false
  let panelText = ''
  const okText = {
    "waiting": "Continue",
    "confirming": "Confirm",
    "successful": "Done",
  }
  switch (status) {
    case 'working':
      showSpinners = true
      headerIcon = <Spin size='large' />
      header = (
        <>
          <h4>Capturing Content</h4>
          <h5>One moment...</h5>
        </>
      )
      subheader = 'Capturing the following messages for Migration'

      break
    case 'waiting':
      header = <h4>Captured Content</h4>
      headerIcon = <CheckCircleTwoTone twoToneColor='#28ff3a' style={{ fontSize: 25 }} />
      subheader = 'Captured the following messages for Migration'
      formDisabled = false
      selectOpacity = 1

      break
    case 'confirming':
      header = <Flex vertical align="flex-start"><h4>Are you sure?</h4><Typography.Text type="secondary">This is your last chance to review</Typography.Text></Flex>
      headerIcon = <ExclamationCircleTwoTone twoToneColor='#c02822' style={{ fontSize: 25 }} />
      subheader = (
        <Flex justify="space-between">
          <Typography.Text type="secondary">Destination: </Typography.Text>
          <Typography.Text strong>{selectedOrg}</Typography.Text>
        </Flex>
        );
      formDisabled = false
      break;
    case 'sending':
      showSpinners = true
      headerIcon = <Spin size='large' />
      header = (
        <>
          <h4>Migrating Layers</h4>
          <h5>One moment...</h5>
        </>
      )
      subheader = 'Sending the following Layers'

      break

    case 'successful':
      header = <h4>Migrated Layers Successfully!</h4>
      headerIcon = <CheckCircleTwoTone twoToneColor='#28ff3a' style={{ fontSize: 25 }} />
      panelColor = '#c0ffbe'
      subheader = 'Didomi Layers are being generated'
      panelText = 'Layers will be available on the Didomi Console shortly...'

      break
    case 'failure':
    case 'server-error':
    case 'screenshot-time-out':
      header = <h4>Failed to Migrate Layers</h4>
      panelColor = 'pink'
      headerIcon = <WarningTwoTone twoToneColor='red' style={{ fontSize: 25 }} />
    case 'screenshot-time-out':
      subheader = 'Screenshot function timed out'
      panelText = 'Check that the Privacy Manager is connected to an active vendor list'
      break
    case 'failure':
      subheader = 'Failed preflight check:'
      panelText = preflightFailureMessage
      break
    case 'server-error':
      subheader = 'There was an internal error in the server'
      panelText = 'Contact administrator if this persists'
      break
  }

  const inProgress = status === 'working' || status === 'waiting' || status === 'sending'

  const panel = (
    <>
      <div className='title'>
        <h3>Migrate to Didomi</h3>
        <Button
          icon={<CloseOutlined />}
          onClick={() => closeModal()}
          variant='link'
        />
      </div>
      <Divider style={{ margin: 3 }} />
      <div className='loading-content'>
        {headerIcon}
        <div className='header-holder'>
          {header}
        </div>
      </div>
      <div className='panel-container'>
        <div className='panel' style={{ background: panelColor }}>
          <div className='panel-title'>
            <h5>
              {subheader}
            </h5>
          </div>
          {inProgress
            ? (
              <>
                {noticeName && (
                  <div className='msg-info'>
                    <div className='msg-info-container'>
                      {showSpinners && <Spin size='small' />}
                      <h6>{noticeName}</h6>
                    </div>
                    <ul>
                      <li>{screenshotText}</li>
                      <li>{htmlText}</li>
                      <li>{cssText}</li>
                    </ul>
                  </div>
                )}
                {pmName && (
                  <>
                  <div className='msg-info'>
                    <div className='msg-info-container'>
                      {showSpinners && <Spin size='small' />}
                      <h6>{pmName} (Preferences)</h6>
                    </div>
                    <ul>
                      <li>{screenshotText}</li>
                      <li>{htmlText}</li>
                      <li>{cssText}</li>
                    </ul>
                  </div>
                  <div className='msg-info'>
                    <div className='msg-info-container'>
                      {showSpinners && <Spin size='small' />}
                      <h6>{pmName} (Vendors)</h6>
                    </div>
                    <ul>
                      <li>{screenshotText}</li>
                      <li>{htmlText}</li>
                      <li>{cssText}</li>
                    </ul>
                  </div>
                </>
              )}
              </>
            )
            : (
              <p className="panel-text">
                {panelText}
              </p>
            )}
        </div>
      </div>

      <div className='panel-container'>
        <div className='panel advisory'>
          <p>
            ⚠️ This action pushes the migration of this message to Didomi.
            It can be performed multiple times — each execution will create duplicates at Didomi.
          </p>
        </div>
      </div>
      {inProgress ? (
        <div className="panel-container">
          <div className="panel select" style={{ opacity: selectOpacity }}>
            <h6>Select Didomi Organization</h6>
            <Select
              disabled={formDisabled}
              style={{ width: "100%" }}
              defaultValue={ddmOrgs[0].ddm_organization_id}
              onChange={(org) => selectOrg(org)}
              options={ddmOrgs.map(({ ddm_organization_id: org }) => ({
                value: org,
                label: org,
              }))}
              value={selectedOrg}
            />
          </div>
        </div>
      ) : null}
      <div className='panel-container'>
        <div className='panel buttons-panel'>
          {inProgress || status === 'confirming'
            ? (
              <>
                <Button
                  primary
                  style={{ width: 75 }}
                  onClick={() => closeModal()}
                >
                  Cancel
                </Button>
                <Button
                  disabled={formDisabled}
                  style={{ width: 75 }}
                  onClick={sendLayers}
                >
                  {okText[status] ?? "Ok"}
                </Button>
              </>
            )
            : (
              <>
                {status !== 'screenshot-time-out' && (
                  <Tooltip title="Download Migration Assets">
                    <Button
                      style={{ borderRadius: 10 }}
                      onClick={zipFiles}
                    >
                      <DownloadOutlined />
                    </Button>
                  </Tooltip>
                )}
                <Button
                  primary
                  style={{ width: 75 }}
                  onClick={() => closeModal()}
                >
                  Ok
                </Button>
              </>
            )}
        </div>
      </div>
    </>
  )

  return panel
}